import { gql, useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import { DatePicker } from '@/components'
import { PATCH_COMPANY_MUTATION } from '@/modules/companies/mutations'
import type {
  PatchCompanyMutation,
  PatchCompanyMutationVariables,
} from '@/modules/companies/types.graphql'
import { format } from '@/utils/date-fns/format'
import { useCompany, useOnErrorAuto } from '@/utils/hooks'

const INFRINGEMENT_GENERATION_START_QUERY = gql`
  query CompanyInfringementGenerationStart {
    currentCompany {
      id
      infringementGenerationStart
    }
  }
`

interface InfringementGenerationStartQuery {
  currentCompany: {
    id: string
    infringementGenerationStart: string | null
  } | null
}

export const InfringementGenerationStart: React.FC = () => {
  const translations = useTranslate({
    placeholder: 'settings.infringements.generation-start-placeholder',
    success: 'settings.toasts.company-settings-updated',
    error: 'settings.errors.company-settings-generic',
  })

  const company = useCompany()
  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery<InfringementGenerationStartQuery>(
    INFRINGEMENT_GENERATION_START_QUERY
  )

  const [patchCompanyMutation, { loading: saving }] = useMutation<
    PatchCompanyMutation,
    PatchCompanyMutationVariables
  >(PATCH_COMPANY_MUTATION, {
    refetchQueries: ['CompanyInfringementGenerationStart'],
    onCompleted: () => {
      addToast('success', translations.success)
    },
    onError: onErrorAuto(translations.error),
  })

  const storedDate = data?.currentCompany?.infringementGenerationStart ?? null

  function handleChange(date: Date | null) {
    void patchCompanyMutation({
      variables: {
        id: company.id,
        input: {
          infringementGenerationStart: date ? format(date, 'yyyy-MM-dd') : null,
        },
      },
    })
  }

  return (
    <DatePicker
      mode="single"
      value={storedDate ? parseISO(storedDate) : null}
      placeholder={translations.placeholder}
      clearable
      typeable
      disabled={loading || saving}
      onChange={handleChange}
    />
  )
}
